import React, {Component} from "react"
import {Row, Col} from "react-bootstrap"
import PropTypes from "prop-types"

import {Body, H3} from "@irati/ui/src/Typography"
import Form from "@irati/ui/src/Form"
import {ValidationButton, Checkbox} from "@irati/ui/src/FormItem"
import Validation from "@irati/utils/validation"
import {Hr} from "@irati/ui/src/Layout"
import Link from "@irati/ui/src/Link"
import {Locale, Office} from "@irati-web/fields/user"
import {FormattedMessage, InjectIntl, injectIntl} from "@irati/utils/lang"
import userService from "@irati/identity/services/users"
import teamService from "@irati/identity/services/teams"
import onboardingService from "@irati/onboarding/services/onboarding"
import OnboardingStep from "@irati/onboarding/enums/Step"
import tracking from "@irati-tracking/platform"
import {MarketingSubscriptionState, UserSource} from "@irati/types"
import {UserInput} from "@irati/types/ops"
import withAuth, {WithAuth} from "@irati/auth/helpers/auth/withAuth"
import {hangPromise} from "@irati/utils/promise"
import {officesForTenant} from "@irati/identity/utils/tenant/hasMultipleOffices"
import Loading from "@irati/ui/src/Loading"
import AuthStream from "@irati/auth/streams/auth"
import History from "@irati/utils/history"
import authentication from "@irati/auth"
import marketingService from "@irati/marketing/services/marketing"

import ContactPreferences from "../ContactPreferences"

const agreeSchema = Validation.bool().oneOf([true], "onboarding.termsAndConditions.checkBox.validation.agreeSchema")

interface Props extends InjectIntl, WithAuth {}

interface State {
  termsAndConditions: boolean | null
  marketingEmailsAllowed: boolean
  loadingUserLanguage?: boolean
  load: Promise<void>
  showRegions: boolean
  saving?: Promise<void>
  office?: string
}

class TermsAndConditions extends Component<Props, State> {
  static contextTypes = {
    apps: PropTypes.object.isRequired
  }

  state: State = {
    termsAndConditions: false,
    marketingEmailsAllowed: true,
    showRegions: false,
    load: hangPromise()
  }

  componentDidMount = () => {
    tracking.onBoarding("Step 3")
    const load = officesForTenant().then((offices) => {
      if (offices.length > 1) {
        const billingUnitIds = offices.map((office) => office.uuid)
        const teamIds = this.props.auth.user.teamIds
        const showRegions = !teamIds.some((id) => billingUnitIds.includes(id))
        this.setState({showRegions})
      }
    })
    this.setState({load})
  }

  handleTermsAndConditions = () => {
    const {termsAndConditions} = this.state
    this.setState({
      termsAndConditions: !termsAndConditions,
      saving: undefined
    })
  }

  handleMarketingEmailEnabled = (marketingEmailsAllowed: boolean) => {
    this.setState({
      marketingEmailsAllowed,
      saving: undefined
    })
  }

  handleSubmit = () => {
    const {marketingEmailsAllowed, office} = this.state
    const {user} = this.props.auth
    const data: UserInput = {
      uuid: user.uuid,
      dateOfBirth: user.dateOfBirth
    }

    const updateUserPromise = userService.update({user: data})
    let promises = [updateUserPromise]

    if (office) {
      promises = [updateUserPromise, teamService.setMyBillingUnit(office)]
    }

    if (!marketingEmailsAllowed) {
      const updateMarketingPreferences = marketingService.getProfile({userId: user.uuid}).then(({signature}) => {
        return marketingService.updateProfile({
          uuid: user.uuid,
          signature,
          settings: {
            emailSubscribed: MarketingSubscriptionState.Unsubscribed,
            pushSubscribed: MarketingSubscriptionState.Unsubscribed
          }
        })
      })
      promises = [...promises, updateMarketingPreferences]
    }

    const saving = Promise.all(promises).then(() => {
      return AuthStream.refresh().then(() => {
        return onboardingService.completeStep(user, OnboardingStep.ACCEPT_GDPR).then(() => {
          tracking.onBoardingComplete()
          this.setState({saving: undefined})
          if (authentication.isGlobalAdmin()) {
            History.push({pathname: "/"}, this.context.apps.ADMIN)
          } else {
            History.push("/")
          }
        })
      })
    })
    this.setState({saving})
  }

  handleLanguageChange = (value: string) => {
    const {user} = this.props.auth
    this.setState({loadingUserLanguage: true})
    userService.update({user: {uuid: user.uuid, language: value}}).then(() => {
      this.setState({loadingUserLanguage: false})
    })
  }

  shouldShowLanguageSelection = () => {
    const {user} = this.props.auth
    if (!user || !user.source) {
      return false
    }
    const showFor = [UserSource.Push, UserSource.Pull, UserSource.Upload]
    return showFor.includes(user.source)
  }

  handleOfficeSelect = (office: string) => this.setState({office})

  render() {
    const {saving, termsAndConditions, marketingEmailsAllowed, user, loadingUserLanguage, showRegions, office, load} = this.state
    const {formatMessage} = this.props

    return (
      <Loading load={load}>
        <Form>
          {user && this.shouldShowLanguageSelection() && (
            <>
              <H3 marginBottom={1}>
                <FormattedMessage id="onboarding.language.heading" />
              </H3>
              <Form>
                <Row>
                  <Col xs={12}>
                    <Locale isLoading={loadingUserLanguage} value={user.language} onChange={this.handleLanguageChange} />
                  </Col>
                </Row>
              </Form>
            </>
          )}
          {showRegions && (
            <>
              <H3 marginBottom={1}>
                <FormattedMessage id="onboarding.location.heading" />
              </H3>
              <Office value={office} onChange={this.handleOfficeSelect} />
            </>
          )}
          <H3 marginBottom={1}>
            <FormattedMessage id="onboarding.termsAndConditions.heading" />
          </H3>
          <Body small marginBottom={2}>
            <FormattedMessage id="onboarding.termsAndConditions.para" />
          </Body>
          <Checkbox
            name="termsAndConditions"
            onChange={this.handleTermsAndConditions}
            checked={termsAndConditions}
            validation={agreeSchema}>
            <FormattedMessage id="onboarding.termsAndConditions.fields.header" />{" "}
            <Link cta href={formatMessage({id: "onboarding.termsAndConditions.fields.link.href"})} target="_blank">
              <FormattedMessage id="onboarding.termsAndConditions.fields.link.text" />
            </Link>
          </Checkbox>
          <Hr />
          <ContactPreferences onChange={this.handleMarketingEmailEnabled} value={marketingEmailsAllowed} />
          <ValidationButton
            primary
            large
            full
            load={saving}
            loading={formatMessage({id: "onboarding.termsAndConditions.button.saving"})}
            onClick={this.handleSubmit}>
            <FormattedMessage id="onboarding.termsAndConditions.button.confirm" />
          </ValidationButton>
        </Form>
      </Loading>
    )
  }
}

export default injectIntl(withAuth(TermsAndConditions))
